import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import AccessError from '../AccessError';

import useAuth from '../../hooks/useAuth';
import useManagerAccess from './useManagerAccess';
import useClientAccess from './useClientAccess';
import useResourceAccess from './useResourceAccess';
import usePartnerAccess from './usePartnerAccess';
import useOnTimeAccess from './useOnTimeAccess';
import { PATH_AUTH } from '../../routes/paths';
import { useAdminStore } from '../../store/useAdminStore';
import LoadingScreen from '../../components/loadings/LoadingScreen';

const RoleBasedGuard = ({ accessibleRoles, requiredUserProps, requiredPermissions, children }) => {
  const {
    env: { settings },
    isAuthenticated,
    user
  } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasPermission, isReady: permissionsReady, userId: storedUserId } = useAdminStore();
  // useAdminBootstrap est appelé en amont via AdminBootstrap (RoutesComponents.jsx).

  const clientAccess = useClientAccess();
  const cometeOnTimeAccess = useOnTimeAccess();
  const managerAccess = useManagerAccess();
  const partnerAccess = usePartnerAccess();
  const resourceAccess = useResourceAccess();

  const userAccess = managerAccess && clientAccess && resourceAccess && partnerAccess && cometeOnTimeAccess;
  const roleAccess = accessibleRoles.includes(user?.role);
  const propsAccess = !requiredUserProps || requiredUserProps.every((prop) => !!user?.[prop]);

  // Au switch d'espace, `user.id` change avant que useAdminBootstrap n'ait
  // clear+setPermissions. Évaluer pendant cette fenêtre flashait "Accès refusé"
  // sur des perms de l'ancien user (CW-21903).
  const permissionsAlignedToUser = !user?.id || storedUserId === user.id;
  const permissionsAccess =
    !requiredPermissions ||
    (!permissionsReady || !permissionsAlignedToUser ? null : requiredPermissions.some((perm) => hasPermission(perm)));

  useEffect(() => {
    if (!settings || !isAuthenticated) {
      const isOnAuthPage = pathname === PATH_AUTH.login || pathname === PATH_AUTH.root || pathname.includes('/login');
      if (!isOnAuthPage) {
        navigate(PATH_AUTH.root);
      }
    }
  }, [settings, isAuthenticated, pathname, navigate]);

  if (isAuthenticated && requiredPermissions && permissionsAccess === null) {
    return <LoadingScreen />;
  }

  // CW-22023 retour QA : pendant la transition d'utilisateur, useAdminBootstrap
  // appelle `clear()` (storedUserId -> null) puis `navigate('/')`. La navigation
  // est async — entre les deux, RoleBasedGuard re-render sur l'URL de l'ancien
  // user avec les settings/permissions du nouveau (env.settings deja a jour cote
  // useAuth). Resultat : useManagerAccess et co. renvoient false sur des pages
  // inaccessibles au nouvel user -> flash AccessError. On affiche LoadingScreen
  // tant que `permissionsAlignedToUser` est faux, le temps que navigate prenne
  // effet et que useAdminBootstrap re-cale storedUserId via setPermissions.
  if (isAuthenticated && user?.id && !permissionsAlignedToUser) {
    return <LoadingScreen />;
  }

  if (isAuthenticated && (!roleAccess || !userAccess || !propsAccess || !permissionsAccess)) {
    return <AccessError />;
  }

  return children;
};

RoleBasedGuard.propTypes = {
  accessibleRoles: PropTypes.array,
  requiredUserProps: PropTypes.arrayOf(PropTypes.string),
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node
};

export default RoleBasedGuard;
